"use client";

import React from "react";
import { Phone, MessageCircle, MapPin, CircleCheck as CheckCircle2, Wrench, Sparkles, Flame, Clock } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PHONE_LOCAL, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MalqaOvenLanding() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    once: true
  });
  const serviceFeatures = [{
    icon: Sparkles,
    title: "إزالة الدهون المستعصية",
    description: "تنظيف عميق لهيكل الفرن، الزجاج الداخلي، والرفوف بمواد متخصصة وآمنة."
  }, {
    icon: Flame,
    title: "تسليك وتنظيف العيون",
    description: "معالجة ضعف الشعلة وانسداد منافذ الغاز لضمان كفاءة واشتعال مثالي."
  }, {
    icon: Wrench,
    title: "صيانة وتغيير قطع الغيار",
    description: "فحص الشوايات، المفاتيح، الصمامات، والمستشعرات مع توفير قطع غيار أصلية للأفران."
  }, {
    icon: Clock,
    title: "خدمة منزلية سريعة بحي الملقا",
    description: "وصول فوري لفريق العمل المجهز إلى منزلك داخل حي الملقا في الوقت المحدد."
  }];
  return <section id="malqaovenservicelanding" data-nav="light" dir="rtl" ref={ref} className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-opacity duration-700" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Top Location Confirmation Banner */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
          <Badge variant="secondary" className="px-3 py-1 text-sm gap-1.5 font-normal">
            <MapPin className="size-4 text-primary" />
            <span>نخدم سكان حي الملقا وكافة أحياء شمال الرياض</span>
          </Badge>
          <Badge variant="outline" className="px-3 py-1 text-sm gap-1.5 border-border">
            <CheckCircle2 className="size-4 text-primary" />
            <span>فحص وصيانة منزلية فورية</span>
          </Badge>
        </div>

        {/* Hero Content Block */}
        <div className="text-center max-w-3xl mx-auto space-y-5">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight">
            خدمة <span className="text-primary">تنظيف أفران حي الملقا</span> والصيانة المنزلية المعتمدة
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            تقدم شركة <strong>لمسة احتراف</strong> خدمات متكاملة في صيانة وغسيل الأفران المنزلية لسكان حي الملقا بالرياض. نقوم بإزالة أصعب الدهون المتراكمة، تسليك العيون، إصلاح الأعطال الفنية للأفران الغازية والكهربائية وتوفير قطع الغيار مباشرة في منزلك دون الحاجة لنقل الفرن.
          </p>

          {/* Action CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-3">
            <Button size="lg" className="w-full sm:w-auto text-base gap-2" asChild>
              <a href={PHONE_TEL_HREF}>
                <Phone className="size-5" />
                اتصال مباشر: {PHONE_LOCAL}
              </a>
            </Button>
            <Button variant="outline" size="lg" className="w-full sm:w-auto text-base gap-2 border-border hover:bg-muted" asChild>
              <Link to={WHATSAPP_HREF} newTab>
                <MessageCircle className="size-5 text-chart-4" />
                محادثة واتساب سريعة
              </Link>
            </Button>
          </div>
        </div>

        {/* Core Capabilities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {serviceFeatures.map((feature, i) => {
          const Icon = feature.icon;
          return <Card key={feature.title} data-index={i} className="border border-border/80 shadow-xs hover:shadow-md transition-shadow bg-card">
                <CardHeader className="space-y-3 pb-2">
                  <div className="w-10 h-10 rounded-md bg-accent flex items-center justify-center text-primary">
                    <Icon className="size-5" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-foreground">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>;
        })}
        </div>

        {/* Local Area Confirmation Card */}
        <div className="mt-12 rounded-xl bg-muted/60 border border-border p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-right w-full md:w-auto">
            <div className="flex items-center gap-2">
              <MapPin className="size-5 text-primary shrink-0" />
              <h2 className="text-xl font-bold text-foreground">
                متواجدون يومياً لخدمة حي الملقا
              </h2>
            </div>
            <p className="text-sm sm:text-base text-muted-foreground">
              نوفر فنيين محترفين مجهزين بأحدث معدات تنظيف أفران حي الملقا وقطع الغيار لفحص الفرن وصيانته فورياً عند الطلب.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
            <Button variant="default" className="w-full sm:w-auto gap-2" asChild>
              <a href={PHONE_TEL_HREF}>
                <Phone className="size-4" />
                اطلب الفني الآن
              </a>
            </Button>
            <Button variant="secondary" className="w-full sm:w-auto gap-2" asChild>
              <Link to={WHATSAPP_HREF} newTab>
                <MessageCircle className="size-4" />
                واتساب
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}